import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { useAMedias, useMovimientosPlan } from '../../context/AMediasContext';
import { useAuth } from '../../context/AuthContext';
import { GastoPlan, PagoPlan } from '../../types';
import {
  calcularSaldos, simplificarDeudas, resumenPorParticipante, totalPorCategoria,
  esCero, formatoEuros, Transferencia,
} from '../../utils/amediasUtils';
import {
  ArrowLeft, ArrowRight, Plus, Edit2, Trash2, Users, Archive, ArchiveRestore,
  MoreVertical, Receipt, Coins, CheckCheck, Wallet, RefreshCw, AlertCircle,
  FileDown, Paperclip, BellRing, AlarmClock, HelpCircle,
} from 'lucide-react';
import { exportarPlanPDF } from '../../utils/pdfPlan';
import { useAvisosAMedias } from '../../context/AvisosAMediasContext';
import { VisorTicket } from '../../components/amedias/VisorTicket';
import {
  estadoRecordatorio, textoRecordatorio, enlaceWhatsApp, pospuestoHasta,
} from '../../utils/recordatoriosAMedias';
import { useData } from '../../context/DataContext';
import { useSincronizacionPlan } from '../../hooks/useSincronizacionPlan';
import { totalCambios, origenesAfectados } from '../../utils/sincronizacionAMedias';
import { ModalSincronizacion } from '../../components/amedias/ModalSincronizacion';
import { ElegirParticipante } from '../../components/amedias/ElegirParticipante';
import { ModalPlan } from '../../components/amedias/ModalPlan';
import { ModalGasto } from '../../components/amedias/ModalGasto';
import { ModalPago } from '../../components/amedias/ModalPago';
import { ModalParticipantes } from '../../components/amedias/ModalParticipantes';
import { fechasPlan } from './Planes';
import { compartirTexto, abrirEnlaceExterno } from '../../utils/plataforma';
import { useConfirmar } from '../../context/ConfirmarContext';

type Pestana = 'movimientos' | 'saldos' | 'resumen';

type Modal =
  | { tipo: 'plan' }
  | { tipo: 'participantes' }
  | { tipo: 'sincronizacion' }
  | { tipo: 'ticket'; gasto: GastoPlan }
  | { tipo: 'gasto'; gasto?: GastoPlan }
  | { tipo: 'pago'; pago?: PagoPlan; sugerido?: Transferencia }
  | null;

type Movimiento =
  | { tipo: 'gasto'; fecha: Date; creado: Date; gasto: GastoPlan }
  | { tipo: 'pago'; fecha: Date; creado: Date; pago: PagoPlan };

const fechaCorta = (d: Date) => format(d, 'dd/MM/yyyy');

export const DetallePlan: React.FC = () => {
  const confirmar = useConfirmar();
  const { planId } = useParams<{ planId: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { planes, loadingPlanes, archivarPlan, eliminarPlan, eliminarGasto, eliminarPago } = useAMedias();
  const { gastos, pagos, cargando } = useMovimientosPlan(planId);

  const [pestana, setPestana] = useState<Pestana>('movimientos');
  const [modal, setModal] = useState<Modal>(null);
  const [menu, setMenu] = useState(false);

  const plan = planes.find(p => p.id === planId);
  // Aquí solo para enseñar el estado y las acciones a mano: quien sincroniza
  // solo es el vigía de AvisosAMedias, que funciona aunque no estés en el plan
  const sync = useSincronizacionPlan(plan, gastos, pagos, !cargando);
  const avisos = useAvisosAMedias();
  const errorFinanzas = sync.error || (planId ? avisos.estadoPlan(planId)?.errorFinanzas ?? '' : '');
  const [params, setParams] = useSearchParams();
  const { cuentas } = useData();
  const [pasando, setPasando] = useState(false);
  // Solo para repintar al posponer el recordatorio (vive en localStorage)
  const [, setPospuesto] = useState(0);

  const saldos = useMemo(() => calcularSaldos(gastos, pagos), [gastos, pagos]);
  const deudas = useMemo(() => simplificarDeudas(saldos), [saldos]);

  // «Ya he pagado» desde un aviso: /amedias/:id?pagar=<a quién> abre el pago rellenado
  const pagarA = params.get('pagar');
  useEffect(() => {
    if (!pagarA || !plan || cargando) return;
    const yoId = plan.participantes.find(p => !!user && p.uid === user.uid)?.id;
    const deuda = deudas.find(d => d.de === yoId && d.a === pagarA);
    if (deuda) setModal({ tipo: 'pago', sugerido: deuda });
    setParams({}, { replace: true });
  }, [pagarA, plan, cargando, deudas, user, setParams]);
  const resumen = useMemo(
    () => resumenPorParticipante(gastos, pagos, plan?.participantes.map(p => p.id) ?? []),
    [gastos, pagos, plan]);
  const porCategoria = useMemo(() => totalPorCategoria(gastos), [gastos]);
  const total = useMemo(() => gastos.reduce((s, g) => s + g.importe, 0), [gastos]);
  const categoriasUsadas = useMemo(
    () => Array.from(new Set(gastos.map(g => g.categoria).filter((c): c is string => !!c))), [gastos]);

  const movimientos: Movimiento[] = useMemo(() => [
    ...gastos.map(g => ({ tipo: 'gasto' as const, fecha: g.fecha, creado: g.createdAt, gasto: g })),
    ...pagos.map(p => ({ tipo: 'pago' as const, fecha: p.fecha, creado: p.createdAt, pago: p })),
  ].sort((a, b) => b.fecha.getTime() - a.fecha.getTime() || b.creado.getTime() - a.creado.getTime()),
  [gastos, pagos]);

  if (loadingPlanes || (plan && cargando)) {
    return <div className="max-w-3xl mx-auto px-4 py-10 text-gray-500 dark:text-gray-400">Cargando…</div>;
  }
  if (!plan) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <p className="text-gray-600 dark:text-gray-400">Este plan no existe o no tienes acceso.</p>
        <Link to="/amedias" className="inline-block mt-4 text-sm font-semibold text-emerald-700 dark:text-emerald-400 hover:underline">
          Volver a mis planes
        </Link>
      </div>
    );
  }

  const yo = plan.participantes.find(p => p.uid === user?.uid);
  const esOrganizador = !!user && plan.organizadoresUids.includes(user.uid);
  // Organizador: todo. Miembro: solo lo que apuntó él.
  const puedeTocar = (creadoPorUid: string) => esOrganizador || creadoPorUid === user?.uid;
  const nombre = (id: string) => {
    const p = plan.participantes.find(x => x.id === id);
    if (!p) return 'Alguien que ya no está';
    return p.id === yo?.id ? 'Tú' : p.nombre;
  };
  const miSaldo = yo ? saldos[yo.id] ?? 0 : 0;
  const nombreReal = (id: string) => plan.participantes.find(x => x.id === id)?.nombre ?? '';

  // ── Recordatorio de deudas ──
  const recordatorio = estadoRecordatorio(plan, gastos);
  const misDeudas = deudas.filter(d => d.de === yo?.id);
  const avisoPendiente = recordatorio.vencido && misDeudas.length > 0 && pospuestoHasta(plan.id) < Date.now();

  const recordar = async (d: Transferencia) => {
    const texto = textoRecordatorio(plan.nombre, nombreReal(d.de), d.importe,
      d.a === yo?.id ? undefined : nombreReal(d.a));
    // En el móvil y en la app, el menú de compartir del sistema; si no, WhatsApp Web
    try {
      if (await compartirTexto({ texto })) return;
      await abrirEnlaceExterno(enlaceWhatsApp(texto));
    } catch (e) {
      console.error('No se pudo compartir el recordatorio', e);
    }
  };

  const borrarGasto = async (g: GastoPlan) => {
    if (!(await confirmar({ titulo: `¿Borrar el gasto «${g.concepto}» de ${formatoEuros(g.importe)}?`, accion: 'Borrar' }))) return;
    await eliminarGasto(plan.id, g);
  };
  const borrarPago = async (p: PagoPlan) => {
    if (!(await confirmar({ titulo: `¿Borrar el pago de ${nombre(p.de)} a ${nombre(p.a)} de ${formatoEuros(p.importe)}?`, accion: 'Borrar' }))) return;
    await eliminarPago(plan.id, p.id);
  };
  const borrarPlan = async () => {
    setMenu(false);
    if (!(await confirmar({
      titulo: `¿Borrar el plan «${plan.nombre}» con todos sus gastos y pagos?`,
      mensaje: 'No se puede deshacer. Si ya está saldado, mejor archívalo y así conservas el historial.' +
        (sync.generadas.length > 0
          ? `\n\nLos ${sync.generadas.length} movimientos que pasaron a tus Finanzas se quedan allí.`
          : ''),
      accion: 'Borrar plan',
    }))) return;
    await eliminarPlan(plan.id);
    navigate('/amedias');
  };

  const botonIcono = 'p-2 rounded-lg text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition';
  const pestanaClase = (p: Pestana) =>
    `flex-1 py-2 text-sm font-semibold border-b-2 transition ${
      pestana === p
        ? 'border-emerald-600 text-emerald-700 dark:text-emerald-400'
        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200'
    }`;
  const itemMenu = 'w-full flex items-center gap-2 px-3 py-2 text-sm text-left hover:bg-gray-50 dark:hover:bg-gray-700';

  return (
    <div className="max-w-3xl mx-auto px-4 py-6">
      <Link to="/amedias" className="inline-flex items-center gap-1 text-sm text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 mb-3">
        <ArrowLeft className="w-4 h-4" /> Mis planes
      </Link>

      {/* ── Cabecera ──────────────────────────────────────────────────────── */}
      <header className="flex items-start gap-3 mb-4">
        <div className="w-14 h-14 shrink-0 rounded-xl bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center text-3xl">
          {plan.icono}
        </div>
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white truncate">
            {plan.nombre}
            {plan.archivado && (
              <span className="ml-2 align-middle text-[10px] font-semibold uppercase tracking-wide text-gray-500
                               bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded-full">Archivado</span>
            )}
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {[fechasPlan(plan), plan.participantes.map(p => p.nombre).join(', ')].filter(Boolean).join(' · ')}
          </p>
          {plan.descripcion && <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">{plan.descripcion}</p>}
        </div>
        <div className="relative shrink-0">
          <button onClick={() => setMenu(m => !m)} aria-label="Opciones del plan" className={botonIcono}>
            <MoreVertical className="w-5 h-5" />
          </button>
          {menu && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setMenu(false)} />
              <div className="absolute right-0 mt-1 z-20 w-52 bg-white dark:bg-gray-800 border border-gray-200
                              dark:border-gray-700 rounded-xl shadow-lg py-1 text-gray-700 dark:text-gray-200">
                <button className={itemMenu} onClick={() => { setMenu(false); setModal({ tipo: 'participantes' }); }}>
                  <Users className="w-4 h-4" /> {esOrganizador ? 'Participantes e invitar' : 'Participantes'}
                </button>
                <button className={itemMenu} onClick={() => { setMenu(false); exportarPlanPDF(plan, gastos, pagos); }}>
                  <FileDown className="w-4 h-4" /> Descargar PDF
                </button>
                <a className={itemMenu} href="/ayuda#amedias" target="_blank" rel="noopener noreferrer" onClick={() => setMenu(false)}>
                  <HelpCircle className="w-4 h-4" /> Cómo funciona
                </a>
                {sync.disponible && yo && (
                  <button className={itemMenu} onClick={() => { setMenu(false); setModal({ tipo: 'sincronizacion' }); }}>
                    <Wallet className="w-4 h-4" /> Pasar a Finanzas
                  </button>
                )}
                {esOrganizador && (
                  <>
                    <button className={itemMenu} onClick={() => { setMenu(false); setModal({ tipo: 'plan' }); }}>
                      <Edit2 className="w-4 h-4" /> Editar plan
                    </button>
                    <button className={itemMenu} onClick={() => { setMenu(false); archivarPlan(plan.id, !plan.archivado); }}>
                      {plan.archivado
                        ? <><ArchiveRestore className="w-4 h-4" /> Sacar de archivados</>
                        : <><Archive className="w-4 h-4" /> Archivar</>}
                    </button>
                    <button className={`${itemMenu} text-red-600 dark:text-red-400`} onClick={borrarPlan}>
                      <Trash2 className="w-4 h-4" /> Borrar plan
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>
      </header>

      {/* Recién llegado por una invitación: primero, decir quién es */}
      {!yo ? <ElegirParticipante plan={plan} /> : (<>

      {/* ── Cifras y acciones ─────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
          <p className="text-xs text-gray-500 dark:text-gray-400">Gasto total</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums">{formatoEuros(total)}</p>
          {yo && (
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Tu parte: {formatoEuros(resumen[yo.id]?.consumido ?? 0)}
            </p>
          )}
        </div>
        <div className={`rounded-xl border p-3 ${
          esCero(miSaldo) ? 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700'
          : miSaldo > 0 ? 'bg-emerald-50 dark:bg-emerald-900/20 border-emerald-200 dark:border-emerald-800'
          : 'bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-800'}`}>
          <p className="text-xs text-gray-500 dark:text-gray-400">Tu saldo</p>
          <p className={`text-xl font-bold tabular-nums ${
            esCero(miSaldo) ? 'text-gray-900 dark:text-white'
            : miSaldo > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
            {esCero(miSaldo) ? formatoEuros(0) : formatoEuros(Math.abs(miSaldo))}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {esCero(miSaldo) ? 'Estás al día' : miSaldo > 0 ? 'Te deben' : 'Debes'}
          </p>
        </div>
      </div>

      {/* ── Recordatorio: ya toca pagar ───────────────────────────────────── */}
      {avisoPendiente && (
        <div className="mb-4 p-3 rounded-xl bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-700
                        text-sm text-amber-900 dark:text-amber-200">
          <p className="flex items-start gap-2">
            <AlarmClock className="w-5 h-5 shrink-0 text-amber-600 dark:text-amber-400" />
            <span>
              Han pasado {recordatorio.dias} días{plan.fechaFin ? ' desde que acabó el plan' : ' desde el último gasto'}.
              Te queda pagar{' '}
              {misDeudas.map((d, i) => (
                <span key={d.a}>{i > 0 && (i === misDeudas.length - 1 ? ' y ' : ', ')}
                  <span className="font-semibold">{formatoEuros(d.importe)}</span> a {nombreReal(d.a)}</span>
              ))}.
            </span>
          </p>
          <div className="flex flex-wrap gap-2 mt-2 ml-7">
            <button onClick={() => setModal({ tipo: 'pago', sugerido: misDeudas[0] })}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white">
              Ya he pagado
            </button>
            <button onClick={() => { avisos.posponer(plan.id); setPospuesto(Date.now()); }}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold text-amber-800 dark:text-amber-300 hover:underline">
              Recordármelo más tarde
            </button>
          </div>
        </div>
      )}

      {/* ── Sincronización con Finanzas ────────────────────────────────────── */}
      {sync.disponible && sync.listo && (
        errorFinanzas ? (
          <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg text-sm bg-red-50 dark:bg-red-900/20
                          border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300">
            <AlertCircle className="w-4 h-4 shrink-0" />
            <span className="flex-1">{errorFinanzas}</span>
            <button onClick={() => setModal({ tipo: 'sincronizacion' })} className="font-semibold hover:underline">Revisar</button>
            <button onClick={() => { sync.reintentar(); avisos.reintentarFinanzas(plan.id); }}
              className="font-semibold hover:underline">Reintentar</button>
          </div>
        ) : sync.config?.activa ? (
          <>
            {totalCambios(sync.pendientes) > 0 && (
              <div className="flex flex-wrap items-center gap-2 mb-2 px-3 py-2 rounded-lg text-sm bg-amber-50 dark:bg-amber-900/20
                              border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-300">
                <RefreshCw className="w-4 h-4 shrink-0" />
                <span className="flex-1">
                  {origenesAfectados(sync.pendientes) === 1
                    ? '1 gasto o pago que apuntó otra persona está pendiente de pasar a tus Finanzas.'
                    : `${origenesAfectados(sync.pendientes)} gastos o pagos que apuntaron otros están pendientes de pasar a tus Finanzas.`}
                </span>
                <button disabled={pasando}
                  onClick={async () => { setPasando(true); await sync.pasarPendientes(); setPasando(false); }}
                  className="px-2.5 py-1 rounded-md text-xs font-semibold bg-amber-600 hover:bg-amber-700 text-white disabled:opacity-60">
                  {pasando ? 'Pasando…' : 'Pasar a Finanzas'}
                </button>
              </div>
            )}
            <button onClick={() => setModal({ tipo: 'sincronizacion' })}
              className="flex items-center gap-1.5 mb-4 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200">
              <Wallet className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
              Pasando tus movimientos a Finanzas
              {cuentas.find(c => c.id === sync.config!.cuentaId) && ` · ${cuentas.find(c => c.id === sync.config!.cuentaId)!.nombre}`}
              {sync.generadas.length > 0 && ` · ${sync.generadas.length} movimientos`}
            </button>
          </>
        ) : (
          <button onClick={() => setModal({ tipo: 'sincronizacion' })}
            className="w-full flex items-center gap-2 mb-4 px-3 py-2 rounded-lg text-sm text-left border border-dashed
                       border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-400
                       hover:border-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-400 transition">
            <Wallet className="w-4 h-4 shrink-0" />
            <span className="flex-1">
              {sync.config ? 'Este plan no se está pasando a Finanzas.' : '¿Pasar tu parte de estos gastos a Finanzas?'}
            </span>
            <span className="font-semibold">{sync.config ? 'Activar' : 'Configurar'}</span>
          </button>
        )
      )}

      <div className="flex gap-2 mb-4">
        <button onClick={() => setModal({ tipo: 'gasto' })}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-lg
                     bg-emerald-600 hover:bg-emerald-700 text-white transition">
          <Plus className="w-4 h-4" /> Gasto
        </button>
        <button onClick={() => setModal({ tipo: 'pago' })}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold rounded-lg
                     border border-emerald-600 text-emerald-700 dark:text-emerald-400
                     hover:bg-emerald-50 dark:hover:bg-emerald-900/20 transition">
          <Coins className="w-4 h-4" /> Pago
        </button>
      </div>

      {/* ── Pestañas ──────────────────────────────────────────────────────── */}
      <div className="flex border-b border-gray-200 dark:border-gray-700 mb-4">
        <button className={pestanaClase('movimientos')} onClick={() => setPestana('movimientos')}>Movimientos</button>
        <button className={pestanaClase('saldos')} onClick={() => setPestana('saldos')}>Saldos</button>
        <button className={pestanaClase('resumen')} onClick={() => setPestana('resumen')}>Resumen</button>
      </div>

      {pestana === 'movimientos' && (
        movimientos.length === 0 ? (
          <div className="text-center py-12 bg-white dark:bg-gray-800 rounded-xl border border-dashed border-gray-300 dark:border-gray-700">
            <Receipt className="w-10 h-10 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-600 dark:text-gray-400 font-medium">Todavía no hay gastos.</p>
            <p className="text-sm text-gray-500 mt-1">Apunta el primero con el botón «Gasto».</p>
          </div>
        ) : (
          <ul className="space-y-2">
            {movimientos.map(m => m.tipo === 'gasto' ? (
              <li key={`g-${m.gasto.id}`} className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3 flex items-start gap-3">
                <div className="w-9 h-9 shrink-0 rounded-lg bg-emerald-50 dark:bg-emerald-900/30 flex items-center justify-center">
                  <Receipt className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-900 dark:text-white truncate">{m.gasto.concepto}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {[
                      yo && yo.id in m.gasto.pagadoPor && Object.keys(m.gasto.pagadoPor).length === 1
                        ? 'Pagaste tú'
                        : `Pagó ${Object.keys(m.gasto.pagadoPor).map(nombre).join(', ')}`,
                      fechaCorta(m.gasto.fecha),
                      m.gasto.categoria,
                    ].filter(Boolean).join(' · ')}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    Entre {Object.keys(m.gasto.importes).length}
                    {yo && (m.gasto.importes[yo.id]
                      ? ` · tu parte ${formatoEuros(m.gasto.importes[yo.id])}`
                      : ' · no participas')}
                  </p>
                </div>
                <p className="font-bold tabular-nums text-gray-900 dark:text-white shrink-0">{formatoEuros(m.gasto.importe)}</p>
                {m.gasto.conTicket && (
                  <button onClick={() => setModal({ tipo: 'ticket', gasto: m.gasto })} title="Ver ticket"
                    className={`${botonIcono} shrink-0 !text-emerald-600 dark:!text-emerald-400`}>
                    <Paperclip className="w-4 h-4" />
                  </button>
                )}
                {puedeTocar(m.gasto.creadoPorUid) && (
                  <div className="flex shrink-0 -mr-1">
                    <button onClick={() => setModal({ tipo: 'gasto', gasto: m.gasto })} title="Editar" className={botonIcono}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => borrarGasto(m.gasto)} title="Borrar"
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </li>
            ) : (
              <li key={`p-${m.pago.id}`} className="bg-gray-50 dark:bg-gray-800/60 rounded-xl border border-dashed border-gray-300 dark:border-gray-700 p-3 flex items-start gap-3">
                <div className="w-9 h-9 shrink-0 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
                  <Coins className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-1 flex-wrap">
                    {nombre(m.pago.de)} <ArrowRight className="w-3.5 h-3.5 text-gray-400" /> {nombre(m.pago.a)}
                  </p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {['Pago', fechaCorta(m.pago.fecha), m.pago.nota].filter(Boolean).join(' · ')}
                  </p>
                </div>
                <p className="font-bold tabular-nums text-gray-700 dark:text-gray-300 shrink-0">{formatoEuros(m.pago.importe)}</p>
                {puedeTocar(m.pago.creadoPorUid) && (
                  <div className="flex shrink-0 -mr-1">
                    <button onClick={() => setModal({ tipo: 'pago', pago: m.pago })} title="Editar" className={botonIcono}>
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button onClick={() => borrarPago(m.pago)} title="Borrar"
                      className="p-2 rounded-lg text-gray-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )
      )}

      {pestana === 'saldos' && (
        <div className="space-y-6">
          <section>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Quién debe a quién</h2>
            {deudas.length === 0 ? (
              <div className="flex items-center gap-2 p-4 rounded-xl bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-sm text-emerald-800 dark:text-emerald-300">
                <CheckCheck className="w-5 h-5" /> Todo el mundo está al día.
              </div>
            ) : (
              <ul className="space-y-2">
                {deudas.map(d => (
                  <li key={`${d.de}-${d.a}`} className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
                    <p className="flex-1 min-w-0 text-sm text-gray-800 dark:text-gray-200">
                      {d.de === yo?.id ? (
                        <>Le debes a <span className="font-semibold">{nombre(d.a)}</span></>
                      ) : d.a === yo?.id ? (
                        <><span className="font-semibold">{nombre(d.de)}</span> te debe</>
                      ) : (
                        <><span className="font-semibold">{nombre(d.de)}</span> le debe a{' '}
                          <span className="font-semibold">{nombre(d.a)}</span></>
                      )}
                    </p>
                    <span className="font-bold tabular-nums text-gray-900 dark:text-white">{formatoEuros(d.importe)}</span>
                    {d.de !== yo?.id && (
                      <button onClick={() => recordar(d)}
                        title={recordatorio.vencido
                          ? `Han pasado ${recordatorio.dias} días: mándale un recordatorio`
                          : 'Mandar un recordatorio por WhatsApp'}
                        className={`p-1.5 rounded-lg border transition ${recordatorio.vencido
                          ? 'border-amber-300 dark:border-amber-700 text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30'
                          : 'border-gray-200 dark:border-gray-700 text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}>
                        <BellRing className="w-3.5 h-3.5" />
                      </button>
                    )}
                    <button onClick={() => setModal({ tipo: 'pago', sugerido: d })}
                      title="Registrar este pago"
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-xs font-semibold
                                 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-700
                                 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition">
                      <CheckCheck className="w-3.5 h-3.5" /> Saldar
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {deudas.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                Es la forma de quedar en paz con menos pagos, aunque no siempre coincida con quién pagó a quién.
              </p>
            )}
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Saldo de cada uno</h2>
            <BarrasSaldo
              filas={plan.participantes.map(p => ({ id: p.id, nombre: nombre(p.id), saldo: saldos[p.id] ?? 0 }))} />
          </section>
        </div>
      )}

      {pestana === 'resumen' && (
        <div className="space-y-6">
          <section>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Por persona</h2>
            <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="w-full text-xs sm:text-sm">
                <thead>
                  <tr className="text-xs text-gray-500 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left font-semibold px-2 sm:px-3 py-2">Persona</th>
                    <th className="text-right font-semibold px-2 sm:px-3 py-2">Ha pagado</th>
                    <th className="text-right font-semibold px-2 sm:px-3 py-2">Su parte</th>
                    <th className="hidden sm:table-cell text-right font-semibold px-3 py-2">Pagos</th>
                    <th className="text-right font-semibold px-2 sm:px-3 py-2">Saldo</th>
                  </tr>
                </thead>
                <tbody className="tabular-nums">
                  {plan.participantes.map(p => {
                    const r = resumen[p.id];
                    if (!r) return null;
                    const neto = r.pagosHechos - r.pagosRecibidos;
                    return (
                      <tr key={p.id} className="border-b last:border-0 border-gray-100 dark:border-gray-700 text-gray-800 dark:text-gray-200">
                        <td className="px-2 sm:px-3 py-2 font-medium">{nombre(p.id)}</td>
                        <td className="px-2 sm:px-3 py-2 text-right">{formatoEuros(r.pagado)}</td>
                        <td className="px-2 sm:px-3 py-2 text-right">{formatoEuros(r.consumido)}</td>
                        <td className="hidden sm:table-cell px-3 py-2 text-right text-gray-500" title="Pagos hechos menos pagos recibidos">
                          {esCero(neto) ? '—' : `${neto > 0 ? '+' : '−'}${formatoEuros(Math.abs(neto))}`}
                        </td>
                        <td className={`px-2 sm:px-3 py-2 text-right font-semibold whitespace-nowrap ${
                          esCero(r.saldo) ? '' : r.saldo > 0 ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                          {esCero(r.saldo) ? formatoEuros(0) : `${r.saldo > 0 ? '+' : '−'}${formatoEuros(Math.abs(r.saldo))}`}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Por categoría</h2>
            {porCategoria.length === 0 ? (
              <p className="text-sm text-gray-500">Sin gastos todavía.</p>
            ) : (
              <ul className="space-y-2">
                {porCategoria.map(c => (
                  <li key={c.categoria}>
                    <div className="flex justify-between text-sm text-gray-800 dark:text-gray-200">
                      <span>{c.categoria}</span>
                      <span className="tabular-nums font-semibold">
                        {formatoEuros(c.importe)}
                        <span className="font-normal text-gray-500 ml-1.5">{Math.round((c.importe / total) * 100)} %</span>
                      </span>
                    </div>
                    <div className="h-2 mt-1 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                      <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${(c.importe / total) * 100}%` }} />
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>
      )}

      </>)}

      {modal?.tipo === 'plan' && <ModalPlan plan={plan} onClose={() => setModal(null)} />}
      {modal?.tipo === 'ticket' && (
        <VisorTicket planId={plan.id} gastoId={modal.gasto.id} titulo={modal.gasto.concepto} onClose={() => setModal(null)} />
      )}
      {modal?.tipo === 'sincronizacion' && (
        <ModalSincronizacion plan={plan} sync={sync} onClose={() => setModal(null)} />
      )}
      {modal?.tipo === 'participantes' && (
        <ModalParticipantes plan={plan} gastos={gastos} pagos={pagos}
          onClose={() => setModal(null)} onSalir={() => navigate('/amedias')} />
      )}
      {modal?.tipo === 'gasto' && (
        <ModalGasto plan={plan} gasto={modal.gasto} categoriasUsadas={categoriasUsadas}
          ticketsEnPlan={gastos.filter(g => g.conTicket).length} onClose={() => setModal(null)} />
      )}
      {modal?.tipo === 'pago' && (
        <ModalPago plan={plan} pago={modal.pago} sugerido={modal.sugerido} onClose={() => setModal(null)} />
      )}
    </div>
  );
};

/** Barra horizontal centrada en cero: a la derecha lo que le deben, a la izquierda lo que debe. */
const BarrasSaldo: React.FC<{ filas: { id: string; nombre: string; saldo: number }[] }> = ({ filas }) => {
  const max = Math.max(...filas.map(f => Math.abs(f.saldo)), 0.01);
  return (
    <ul className="space-y-2 bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-3">
      {filas.map(f => {
        const ancho = `${(Math.abs(f.saldo) / max) * 50}%`;
        const positivo = f.saldo > 0;
        return (
          <li key={f.id} className="grid grid-cols-[6rem_1fr_6rem] items-center gap-2 text-sm">
            <span className="truncate text-gray-800 dark:text-gray-200">{f.nombre}</span>
            <div className="relative h-3">
              <div className="absolute inset-y-0 left-1/2 w-px bg-gray-300 dark:bg-gray-600" />
              {!esCero(f.saldo) && (
                <div className={`absolute inset-y-0 rounded ${positivo ? 'left-1/2 bg-emerald-500' : 'right-1/2 bg-red-400'}`}
                  style={{ width: ancho }} />
              )}
            </div>
            <span className={`text-right tabular-nums font-semibold ${
              esCero(f.saldo) ? 'text-gray-500' : positivo ? 'text-emerald-700 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
              {esCero(f.saldo) ? formatoEuros(0) : `${positivo ? '+' : '−'}${formatoEuros(Math.abs(f.saldo))}`}
            </span>
          </li>
        );
      })}
    </ul>
  );
};
